import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Plus,
  FileText,
  Save,
  Eye,
  User,
  MapPin,
  Trash2,
  GripVertical,
  Loader2,
  CheckCircle,
  Image,
  Table,
  Upload,
  BookOpen,
  Scale,
  Settings,
  Copy,
  ChevronDown,
  ChevronUp,
  FileImage,
  List,
  Mic,
  X,
  ArrowRight,
  Printer
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==================== BIBLIOTHÈQUE DE CONTENUS RÉUTILISABLES ====================

const contentLibrary = {
  // Sections types de rapport
  sections: [
    { id: 'objet', title: "1. Objet de la mission", category: 'structure' },
    { id: 'contexte_reg', title: "2. Contexte réglementaire", category: 'structure' },
    { id: 'methodologie', title: "3. Méthodologie de mesure", category: 'structure' },
    { id: 'resultats', title: "4. Résultats des mesures", category: 'structure' },
    { id: 'analyse', title: "5. Analyse des émergences", category: 'structure' },
    { id: 'interpretation', title: "6. Interprétation et commentaires", category: 'structure' },
    { id: 'conclusion', title: "7. Conclusion", category: 'structure' },
    { id: 'annexes', title: "8. Annexes", category: 'structure' },
    { id: 'photos', title: "9. Plans et photos", category: 'structure' },
  ],
  
  // Textes réglementaires pré-rédigés
  reglementation: [
    {
      id: 'reg_voisinage',
      title: "Bruit de voisinage - Décret 2006-1099",
      content: `Les mesures présentées dans ce rapport s'appuient sur les références réglementaires suivantes :
• Décret n° 2006-1099 du 31 août 2006 relatif à la lutte contre les bruits de voisinage
• Code de la santé publique (articles R.1336-6 à R.1336-10)
• Norme NF S31-010 relative à la caractérisation et au mesurage des bruits de l'environnement

Les valeurs limites d'émergence sont fixées à :
- 5 dB(A) en période diurne (7h-22h)
- 3 dB(A) en période nocturne (22h-7h)`
    },
    {
      id: 'reg_erp_musique',
      title: "ERP Musique amplifiée - Décret 2017-1244",
      content: `Conformément au Décret n° 2017-1244 du 7 août 2017 et à l'Arrêté du 17 avril 2023 relatif à la prévention des risques liés aux bruits et aux sons amplifiés :

Niveaux limites à l'émission :
• LAeq 15min ≤ 102 dB(A)
• LCeq 15min ≤ 118 dB(C)

Ces valeurs sont mesurées à tout emplacement où le public a accès.
Un limiteur de pression acoustique doit être installé et réglé selon ces valeurs.`
    },
    {
      id: 'reg_icpe',
      title: "ICPE - Arrêté du 23/01/1997",
      content: `Arrêté du 23 janvier 1997 relatif à la limitation des bruits émis dans l'environnement par les ICPE :

Les émissions sonores ne doivent pas engendrer une émergence supérieure aux valeurs admissibles :
• Période jour (7h-22h) : 5 dB(A) si LAeq > 35 dB(A)
• Période nuit (22h-7h) : 3 dB(A) si LAeq > 30 dB(A)

De plus, les niveaux de bruit en limite de propriété ne doivent pas dépasser :
• 70 dB(A) de jour
• 60 dB(A) de nuit`
    },
    {
      id: 'reg_chantier',
      title: "Chantiers BTP - Décret 2006-1099",
      content: `Réglementation applicable aux chantiers de construction :

• Décret n° 2006-1099 du 31 août 2006
• Arrêtés préfectoraux fixant les horaires autorisés
• Code de l'environnement (articles R.571-1 à R.571-10)

Généralement, les travaux bruyants sont autorisés :
- Du lundi au vendredi : 7h à 20h
- Le samedi : 8h à 12h
- Interdits les dimanches et jours fériés

L'utilisation d'engins et matériels conformes aux normes de bruit est obligatoire.`
    }
  ],
  
  // Glossaire technique
  glossaire: [
    { terme: "LAeq", definition: "Niveau acoustique équivalent pondéré A, représentant l'énergie acoustique moyenne sur une durée T" },
    { terme: "L50", definition: "Niveau de pression acoustique dépassé pendant 50% du temps de mesure (niveau médian)" },
    { terme: "L90", definition: "Niveau de pression acoustique dépassé pendant 90% du temps (bruit de fond)" },
    { terme: "Lmax", definition: "Niveau maximal mesuré pendant la période d'observation" },
    { terme: "Lpeak", definition: "Niveau de crête, valeur maximale instantanée de la pression acoustique" },
    { terme: "Émergence", definition: "Différence entre le niveau de bruit ambiant (source en fonctionnement) et le niveau résiduel (source à l'arrêt)" },
    { terme: "Bruit ambiant", definition: "Bruit total existant dans une situation donnée, composé du bruit de la source et du bruit résiduel" },
    { terme: "Bruit résiduel", definition: "Bruit restant en l'absence du bruit particulier de la source incriminée" },
    { terme: "Bruit particulier", definition: "Composante du bruit ambiant qui peut être identifiée et associée à une source déterminée" },
    { terme: "ZER", definition: "Zone à Émergence Réglementée - zone où l'émergence est limitée réglementairement" },
    { terme: "Tonalité marquée", definition: "Perception d'une composante fréquentielle émergente (sifflement, bourdonnement)" },
    { terme: "dB(A)", definition: "Décibel pondéré A - pondération fréquentielle simulant la sensibilité de l'oreille humaine" },
    { terme: "dB(C)", definition: "Décibel pondéré C - pondération utilisée notamment pour les niveaux de crête" },
  ],
  
  // Équipements de mesure (à personnaliser)
  equipements: [
    { 
      id: 'sono_classe1', 
      nom: "Sonomètre Classe 1", 
      description: "Sonomètre intégrateur de classe 1 conforme à la norme IEC 61672-1",
      marque: "",
      modele: "",
      numero_serie: ""
    },
    { 
      id: 'calibreur', 
      nom: "Calibreur acoustique", 
      description: "Calibreur de niveau 94 dB à 1000 Hz, classe 1 conforme IEC 60942",
      marque: "",
      modele: "",
      numero_serie: ""
    },
  ],
  
  // Méthodologies types
  methodologies: [
    {
      id: 'methodo_emergence',
      title: "Mesure d'émergence (NF S31-010)",
      content: `Les mesures ont été réalisées conformément à la norme NF S31-010 :

1. **Dispositif de mesure**
Les mesures ont été réalisées à l'aide d'un sonomètre de classe 1, conforme à la norme IEC 61672-1, étalonné avant et après la campagne.

2. **Conditions de mesure**
- Vent inférieur à 5 m/s
- Absence de précipitations
- Microphone équipé d'une boule anti-vent

3. **Paramètres enregistrés**
- LAeq : niveau de pression acoustique équivalent pondéré A
- L50 : niveau médian
- Lmax : niveau maximal
- Spectre par bande d'octave (63 Hz à 8 kHz)

4. **Durée des mesures**
Intégration sur des périodes représentatives d'au moins 30 minutes.`
    },
    {
      id: 'methodo_musique',
      title: "Vérification limiteur musique amplifiée",
      content: `Méthodologie de vérification conforme au Décret 2017-1244 :

1. **Points de mesure**
Mesures réalisées en tout point accessible au public, à hauteur d'oreille (1,50 m).

2. **Paramètres vérifiés**
- LAeq,15min ≤ 102 dB(A)
- LCeq,15min ≤ 118 dB(C)

3. **Protocole**
- Mesure pendant les passages musicaux les plus énergétiques
- Intégration sur 15 minutes
- Plusieurs points de mesure dans la salle`
    }
  ]
};

// ==================== COMPOSANT PRINCIPAL ====================

const ReportEditorV2 = () => {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const fileInputRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [libraryTab, setLibraryTab] = useState('sections');
  const [showAddToLibrary, setShowAddToLibrary] = useState(false);
  const [newLibraryItem, setNewLibraryItem] = useState({ title: '', content: '', category: 'custom' });
  const [customLibrary, setCustomLibrary] = useState([]);
  const [showHelp, setShowHelp] = useState(false);
  const [showAssistant, setShowAssistant] = useState(false);
  const [assistantMessage, setAssistantMessage] = useState('');
  const isEditMode = Boolean(reportId);

  // Autocomplétion destinataire et site
  const [clientSearch, setClientSearch] = useState('');
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const [siteSearch, setSiteSearch] = useState('');
  const [siteSuggestions, setSiteSuggestions] = useState([]);
  const [showSiteSuggestions, setShowSiteSuggestions] = useState(false);
  
  // Aperçu temps réel
  const [showPreview, setShowPreview] = useState(false);

  // Historique pour Ctrl+Z / Ctrl+Y
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const maxHistory = 50;

  // État du rapport
  const [report, setReport] = useState({
    title: '',
    reference: '',
    type: 'Mesures acoustiques',
    status: 'brouillon',
    destinataire: {
      nom: '',
      societe: '',
      adresse: '',
      email: '',
      telephone: ''
    },
    site: {
      nom: '',
      adresse: '',
      type: ''
    },
    periode: {
      debut: '',
      fin: ''
    },
    sections: [],
    images: [],
    tableaux: [],
    annexes: [],
    ressources: {
      dossiers: [
        { id: 'photos', name: 'Photos', type: 'images', files: [] },
        { id: 'documents', name: 'Documents', type: 'files', files: [] },
        { id: 'certificats', name: 'Certificats', type: 'files', files: [] }
      ]
    }
  });

  // État pour le gestionnaire de ressources
  const [selectedDossier, setSelectedDossier] = useState('photos');
  const [showRessourcesManager, setShowRessourcesManager] = useState(false);
  const [newDossierName, setNewDossierName] = useState('');

  // Charger le rapport si en mode édition
  useEffect(() => {
    if (reportId) {
      loadReport();
    }
    loadCustomLibrary();
  }, [reportId]);

  // Gestion de l'historique pour Ctrl+Z
  const saveToHistory = (newReport) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.stringify(newReport));
    if (newHistory.length > maxHistory) {
      newHistory.shift();
    }
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  // Undo (Ctrl+Z)
  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setReport(JSON.parse(history[newIndex]));
      toast.info('Action annulée');
    }
  };

  // Redo (Ctrl+Y)
  const redo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setReport(JSON.parse(history[newIndex]));
      toast.info('Action rétablie');
    }
  };

  // Raccourcis clavier
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        redo();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        saveReport();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [historyIndex, history]);

  // Charger la bibliothèque personnelle
  const loadCustomLibrary = async () => {
    try {
      const token = localStorage.getItem('tkale_token');
      const response = await axios.get(`${API}/acoustics/library`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomLibrary(response.data || []);
    } catch (error) {
      console.error('Erreur chargement bibliothèque:', error);
    }
  };

  // Recherche de clients pour autocomplétion
  const searchClients = async (query) => {
    if (query.length < 2) {
      setClientSuggestions([]);
      setShowClientSuggestions(false);
      return;
    }
    try {
      const token = localStorage.getItem('tkale_token');
      const response = await axios.get(`${API}/acoustics/search/clients?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientSuggestions(response.data || []);
      setShowClientSuggestions(true);
    } catch (error) {
      console.error('Erreur recherche clients:', error);
    }
  };

  // Recherche de sites pour autocomplétion
  const searchSites = async (query) => {
    if (query.length < 2) {
      setSiteSuggestions([]);
      setShowSiteSuggestions(false);
      return;
    }
    try {
      const token = localStorage.getItem('tkale_token');
      const response = await axios.get(`${API}/acoustics/search/sites?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSiteSuggestions(response.data || []);
      setShowSiteSuggestions(true);
    } catch (error) {
      console.error('Erreur recherche sites:', error);
    }
  };

  // Sélection d'un client depuis les suggestions
  const selectClient = (client) => {
    setReport(prev => ({
      ...prev,
      destinataire: {
        nom: client.name || '',
        societe: client.name || '',
        adresse: `${client.address || ''} ${client.postal_code || ''} ${client.city || ''}`.trim(),
        email: client.email || '',
        telephone: client.phone || ''
      }
    }));
    setClientSearch(client.name || '');
    setShowClientSuggestions(false);
    toast.success(`Client "${client.name}" sélectionné`);
  };

  // Sélection d'un site depuis les suggestions
  const selectSite = (site) => {
    setReport(prev => ({
      ...prev,
      site: {
        nom: site.nom || '',
        adresse: site.adresse || '',
        type: site.type || ''
      }
    }));
    setSiteSearch(site.nom || '');
    setShowSiteSuggestions(false);
    toast.success(`Site "${site.nom}" sélectionné`);
  };

  // Télécharger PDF - ouvre dans nouvelle fenêtre
  const downloadPdfViaPost = () => {
    if (!reportId) {
      toast.error('Sauvegardez d\'abord le rapport');
      return;
    }
    
    const token = localStorage.getItem('tkale_token');
    if (!token) {
      toast.error('Session expirée');
      return;
    }
    
    const url = `${API}/acoustics/reports/${reportId}/pdf/download?token=${encodeURIComponent(token)}`;
    window.open(url, '_blank');
  };

  // Imprimer l'aperçu
  const handlePrintPreview = () => {
    const content = document.getElementById('preview-print-content');
    if (!content) {
      toast.error('Contenu non trouvé');
      return;
    }
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Rapport - ${report.title || 'Sans titre'}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
          h1 { color: #0891b2; border-bottom: 2px solid #0891b2; padding-bottom: 10px; }
          h2 { color: #1e293b; margin-top: 30px; }
          .section-number { background: #0891b2; color: white; padding: 4px 12px; border-radius: 50%; margin-right: 10px; }
          .header-info { display: flex; justify-content: space-between; margin-bottom: 30px; padding: 20px; background: #f8fafc; }
          .header-info div { flex: 1; }
          .header-info p { margin: 5px 0; }
          .section { margin-bottom: 30px; page-break-inside: avoid; }
          .section-content { margin-left: 40px; line-height: 1.6; white-space: pre-wrap; }
          img { max-width: 100%; height: auto; margin: 10px 0; }
          .footer { margin-top: 50px; text-align: center; color: #64748b; border-top: 1px solid #e2e8f0; padding-top: 20px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <h1>${report.title || 'Rapport'}</h1>
        <div class="header-info">
          <div>
            <p><strong>Référence:</strong> ${report.reference || '-'}</p>
            <p><strong>Destinataire:</strong> ${report.destinataire?.societe || '-'}</p>
          </div>
          <div>
            <p><strong>Site:</strong> ${report.site?.nom || '-'}</p>
            <p><strong>Date:</strong> ${new Date().toLocaleDateString('fr-FR')}</p>
          </div>
        </div>
        ${(report.sections || []).map((s, i) => `
          <div class="section">
            <h2><span class="section-number">${i + 1}</span>${s.title || 'Section'}</h2>
            <div class="section-content">${s.content || ''}</div>
            ${(report.images || []).filter(img => img.sectionId === s.id).map(img => 
              `<img src="${img.data}" alt="${img.name}" />`
            ).join('')}
          </div>
        `).join('')}
        <div class="footer">
          <p>Rapport généré le ${new Date().toLocaleDateString('fr-FR')} • CF AUDIO</p>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // Ajouter un élément à la bibliothèque
  const addToLibrary = async () => {
    if (!newLibraryItem.title || !newLibraryItem.content) {
      toast.error('Titre et contenu requis');
      return;
    }
    try {
      const token = localStorage.getItem('tkale_token');
      await axios.post(`${API}/acoustics/library`, newLibraryItem, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ajouté à la bibliothèque !');
      setShowAddToLibrary(false);
      setNewLibraryItem({ title: '', content: '', category: 'custom' });
      loadCustomLibrary();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
      console.error(error);
    }
  };

  // Sauvegarder une section existante dans la bibliothèque
  const saveSectionToLibrary = async (section) => {
    try {
      const token = localStorage.getItem('tkale_token');
      await axios.post(`${API}/acoustics/library`, {
        title: section.title,
        content: section.content,
        category: 'custom'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`"${section.title}" sauvegardé dans la bibliothèque !`);
      loadCustomLibrary();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      console.error(error);
    }
  };

  // Supprimer un élément de la bibliothèque
  const deleteFromLibrary = async (itemId) => {
    try {
      const token = localStorage.getItem('tkale_token');
      await axios.delete(`${API}/acoustics/library/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Élément supprimé de la bibliothèque');
      loadCustomLibrary();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
      console.error(error);
    }
  };

  const loadReport = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('tkale_token');
      const response = await axios.get(`${API}/acoustics/reports/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Merger les données reçues avec la structure par défaut
      const data = response.data;
      setReport(prev => ({
        ...prev,
        title: data.title || '',
        reference: data.reference || '',
        type: data.type || 'Mesures acoustiques',
        status: data.status || 'brouillon',
        destinataire: {
          nom: data.destinataire?.nom || '',
          societe: data.destinataire?.societe || '',
          adresse: data.destinataire?.adresse || '',
          email: data.destinataire?.email || '',
          telephone: data.destinataire?.telephone || ''
        },
        site: {
          nom: data.site?.nom || '',
          adresse: data.site?.adresse || '',
          type: data.site?.type || ''
        },
        periode: {
          debut: data.periode?.debut || '',
          fin: data.periode?.fin || ''
        },
        sections: Array.isArray(data.sections) ? data.sections : [],
        images: Array.isArray(data.images) ? data.images : [],
        tableaux: Array.isArray(data.tableaux) ? data.tableaux : []
      }));
    } catch (error) {
      toast.error('Erreur lors du chargement du rapport');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // ==================== GESTION DES SECTIONS ====================

  const addSection = (title = 'Nouvelle section', content = '') => {
    const newSection = {
      id: `section-${Date.now()}`,
      title,
      content,
      type: 'text',
      order: (report.sections || []).length
    };
    const newReport = {
      ...report,
      sections: [...(report.sections || []), newSection]
    };
    saveToHistory(newReport);
    setReport(newReport);
    toast.success(`Section "${title}" ajoutée`);
  };

  const addSectionFromLibrary = (item, type) => {
    if (type === 'sections') {
      addSection(item.title, '');
    } else if (type === 'reglementation' || type === 'methodologies' || type === 'custom') {
      addSection(item.title, item.content || '');
    } else if (type === 'glossaire') {
      // Ajouter tout le glossaire
      const glossaireContent = contentLibrary.glossaire
        .map(g => `**${g.terme}** : ${g.definition}`)
        .join('\n\n');
      addSection('Glossaire technique', glossaireContent);
    }
    setShowLibrary(false);
  };

  const updateSection = (sectionId, field, value) => {
    setReport(prev => ({
      ...prev,
      sections: (prev.sections || []).map(s =>
        s.id === sectionId ? { ...s, [field]: value } : s
      )
    }));
    // Note: On ne sauvegarde pas chaque frappe dans l'historique pour éviter de le saturer
  };

  const deleteSection = (sectionId) => {
    const newReport = {
      ...report,
      sections: (report.sections || []).filter(s => s.id !== sectionId)
    };
    saveToHistory(newReport);
    setReport(newReport);
    toast.success('Section supprimée');
  };

  const moveSection = (index, direction) => {
    const newSections = [...report.sections];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newSections.length) return;
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    setReport(prev => ({ ...prev, sections: newSections }));
  };

  const duplicateSection = (section) => {
    const newSection = {
      ...section,
      id: `section-${Date.now()}`,
      title: `${section.title} (copie)`
    };
    setReport(prev => ({
      ...prev,
      sections: [...prev.sections, newSection]
    }));
    toast.success('Section dupliquée');
  };

  // ==================== GESTION DES IMAGES ====================

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} n'est pas une image`);
        continue;
      }

      // Convertir en base64 pour stockage local
      const reader = new FileReader();
      reader.onload = (event) => {
        const newImage = {
          id: `img-${Date.now()}`,
          name: file.name,
          data: event.target.result,
          caption: ''
        };
        setReport(prev => ({
          ...prev,
          images: [...(prev.images || []), newImage]
        }));
        toast.success(`Image "${file.name}" ajoutée`);
      };
      reader.readAsDataURL(file);
    }
  };

  // Import d'image directement dans une section
  const handleImageUploadForSection = (e, sectionId) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (!file.type.startsWith('image/')) {
      toast.error(`${file.name} n'est pas une image`);
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const imageData = event.target.result;
      const imageId = `img-${Date.now()}`;
      
      // Ajouter l'image à la liste globale
      const newImage = {
        id: imageId,
        name: file.name,
        data: imageData,
        caption: '',
        sectionId: sectionId
      };
      
      // Insérer directement dans le contenu de la section
      setReport(prev => ({
        ...prev,
        images: [...(prev.images || []), newImage],
        sections: (prev.sections || []).map(s =>
          s.id === sectionId 
            ? { ...s, content: s.content + `\n\n[IMAGE: ${file.name}]\n\n` }
            : s
        )
      }));
      
      toast.success(`Image "${file.name}" insérée dans la section`);
    };
    reader.readAsDataURL(file);
  };

  const insertImageInSection = (sectionId, imageId) => {
    const image = (report.images || []).find(img => img.id === imageId);
    if (!image) return;
    
    const placeholder = `\n\n[IMAGE: ${image.name}]\n\n`;
    setReport(prev => ({
      ...prev,
      sections: (prev.sections || []).map(s =>
        s.id === sectionId ? { ...s, content: (s.content || '') + placeholder } : s
      )
    }));
    toast.success('Image insérée dans la section');
  };

  const deleteImage = (imageId) => {
    setReport(prev => ({
      ...prev,
      images: (prev.images || []).filter(img => img.id !== imageId)
    }));
    toast.success('Image supprimée');
  };

  // ==================== GESTION DES RESSOURCES (DOSSIERS) ====================

  // Créer un nouveau dossier
  const createDossier = (name) => {
    if (!name.trim()) return;
    
    const newDossier = {
      id: `dossier-${Date.now()}`,
      name: name.trim(),
      type: 'mixed',
      files: []
    };
    
    setReport(prev => ({
      ...prev,
      ressources: {
        ...prev.ressources,
        dossiers: [...(prev.ressources?.dossiers || []), newDossier]
      }
    }));
    
    setNewDossierName('');
    toast.success(`Dossier "${name}" créé`);
  };

  // Supprimer un dossier
  const deleteDossier = (dossierId) => {
    if (!window.confirm('Supprimer ce dossier et son contenu ?')) return;
    
    setReport(prev => ({
      ...prev,
      ressources: {
        ...prev.ressources,
        dossiers: (prev.ressources?.dossiers || []).filter(d => d.id !== dossierId)
      }
    }));
    
    if (selectedDossier === dossierId) {
      setSelectedDossier('photos');
    }
    toast.success('Dossier supprimé');
  };

  // Ajouter un fichier à un dossier
  const addFileToDossier = (dossierId, file, fileData) => {
    const newFile = {
      id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: file.name,
      type: file.type,
      size: file.size,
      data: fileData,
      uploadedAt: new Date().toISOString(),
      legende: ''
    };
    
    setReport(prev => ({
      ...prev,
      ressources: {
        ...prev.ressources,
        dossiers: (prev.ressources?.dossiers || []).map(d =>
          d.id === dossierId
            ? { ...d, files: [...d.files, newFile] }
            : d
        )
      }
    }));
    
    return newFile;
  };

  // Upload de fichiers vers un dossier
  const handleDossierUpload = (e, dossierId) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        addFileToDossier(dossierId, file, event.target.result);
        toast.success(`"${file.name}" ajouté au dossier`);
      };
      reader.readAsDataURL(file);
    });
    
    e.target.value = '';
  };

  // Supprimer un fichier d'un dossier
  const deleteFileFromDossier = (dossierId, fileId) => {
    setReport(prev => ({
      ...prev,
      ressources: {
        ...prev.ressources,
        dossiers: (prev.ressources?.dossiers || []).map(d =>
          d.id === dossierId
            ? { ...d, files: d.files.filter(f => f.id !== fileId) }
            : d
        )
      }
    }));
    toast.success('Fichier supprimé');
  };

  // Mettre à jour la légende d'un fichier
  const updateFileLegende = (dossierId, fileId, legende) => {
    setReport(prev => ({
      ...prev,
      ressources: {
        ...prev.ressources,
        dossiers: (prev.ressources?.dossiers || []).map(d =>
          d.id === dossierId
            ? { ...d, files: d.files.map(f => f.id === fileId ? { ...f, legende } : f) }
            : d
        )
      }
    }));
  };

  // Créer une annexe à partir d'un dossier
  const createAnnexeFromDossier = (dossierId) => {
    const dossier = (report.ressources?.dossiers || []).find(d => d.id === dossierId);
    if (!dossier || dossier.files.length === 0) {
      toast.error('Le dossier est vide');
      return;
    }
    
    const annexe = {
      id: `annexe-${Date.now()}`,
      title: `Annexe - ${dossier.name}`,
      type: 'dossier',
      dossierId: dossierId,
      files: dossier.files.map(f => ({ ...f })),
      createdAt: new Date().toISOString()
    };
    
    setReport(prev => ({
      ...prev,
      annexes: [...(prev.annexes || []), annexe]
    }));
    
    toast.success(`Annexe "${annexe.title}" créée avec ${dossier.files.length} fichier(s)`);
  };

  // Insérer un fichier d'un dossier dans une section
  const insertFileInSection = (sectionId, dossierId, fileId) => {
    const dossier = (report.ressources?.dossiers || []).find(d => d.id === dossierId);
    const file = dossier?.files.find(f => f.id === fileId);
    
    if (!file) return;
    
    if (file.type.startsWith('image/')) {
      // Ajouter comme image avec sectionId
      const newImage = {
        id: `img-${Date.now()}`,
        name: file.name,
        data: file.data,
        caption: file.legende || '',
        sectionId: sectionId
      };
      
      setReport(prev => ({
        ...prev,
        images: [...(prev.images || []), newImage],
        sections: (prev.sections || []).map(s =>
          s.id === sectionId
            ? { ...s, content: s.content + `\n\n[IMAGE: ${file.name}]\n\n` }
            : s
        )
      }));
      
      toast.success(`Image "${file.name}" insérée dans la section`);
    } else {
      // Pour les autres fichiers, ajouter une référence
      setReport(prev => ({
        ...prev,
        sections: (prev.sections || []).map(s =>
          s.id === sectionId
            ? { ...s, content: s.content + `\n\n[FICHIER: ${file.name}]\n\n` }
            : s
        )
      }));
      
      toast.success(`Référence au fichier "${file.name}" ajoutée`);
    }
  };

  // Upload d'annexes PDF
  const handleAnnexeUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    files.forEach(file => {
      if (file.type !== 'application/pdf') {
        toast.error(`${file.name} n'est pas un PDF`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const annexe = {
          id: `annexe-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: file.name,
          data: event.target.result,
          size: file.size,
          uploadedAt: new Date().toISOString()
        };
        
        setReport(prev => ({
          ...prev,
          annexes: [...(prev.annexes || []), annexe]
        }));
        toast.success(`Annexe "${file.name}" ajoutée`);
      };
      reader.readAsDataURL(file);
    });
    
    e.target.value = '';
  };

  const deleteAnnexe = (annexeId) => {
    setReport(prev => ({
      ...prev,
      annexes: (prev.annexes || []).filter(a => a.id !== annexeId)
    }));
    toast.success('Annexe supprimée');
  };

  // Import de fichier universel dans une section
  const handleFileImportForSection = (e, sectionId) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileName = file.name;
    const fileExt = fileName.split('.').pop()?.toLowerCase();
    
    // Fichiers texte lisibles directement
    const textExtensions = ['txt', 'md', 'csv', 'json', 'xml', 'html', 'css', 'js'];
    
    if (textExtensions.includes(fileExt)) {
      // Lire et insérer le contenu texte
      const reader = new FileReader();
      reader.onload = (event) => {
        const textContent = event.target.result;
        setReport(prev => ({
          ...prev,
          sections: (prev.sections || []).map(s =>
            s.id === sectionId 
              ? { ...s, content: (s.content || '') + `\n\n--- Contenu importé de ${fileName} ---\n\n${textContent}\n\n` }
              : s
          )
        }));
        toast.success(`Contenu de "${fileName}" inséré dans la section`);
      };
      reader.readAsText(file);
    } else if (file.type.startsWith('image/')) {
      // C'est une image
      handleImageUploadForSection(e, sectionId);
    } else {
      // Autres fichiers (PDF, DOCX, etc.) - stocker comme pièce jointe
      const reader = new FileReader();
      reader.onload = (event) => {
        const fileData = event.target.result;
        const newAttachment = {
          id: `file-${Date.now()}`,
          name: fileName,
          type: file.type,
          size: file.size,
          data: fileData,
          sectionId: sectionId
        };
        
        setReport(prev => ({
          ...prev,
          attachments: [...(prev.attachments || []), newAttachment],
          sections: (prev.sections || []).map(s =>
            s.id === sectionId 
              ? { ...s, content: (s.content || '') + `\n\n[FICHIER JOINT: ${fileName}]\n\n` }
              : s
          )
        }));
        toast.success(`Fichier "${fileName}" joint à la section`);
      };
      reader.readAsDataURL(file);
    }
  };

  // ==================== SAUVEGARDE ====================

  const saveReport = async () => {
    if (!report.title) {
      toast.error('Veuillez saisir un titre pour le rapport');
      return;
    }

    try {
      setIsSaving(true);
      const token = localStorage.getItem('tkale_token');
      
      const endpoint = isEditMode 
        ? `${API}/acoustics/reports/${reportId}`
        : `${API}/acoustics/reports`;
      
      const method = isEditMode ? 'put' : 'post';
      
      await axios[method](endpoint, report, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSaveSuccess(true);
      toast.success(isEditMode ? 'Rapport mis à jour' : 'Rapport créé');
      
      setTimeout(() => setSaveSuccess(false), 3000);
      
      if (!isEditMode) {
        navigate('/acoustics/reports');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  // ==================== RENDU ====================

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <TooltipProvider>
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header fixe */}
      <header className="sticky top-0 z-50 border-b border-slate-700/50 bg-slate-900/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/acoustics/reports')}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Retour
              </Button>
              <div>
                <h1 className="text-xl font-bold text-white">
                  {isEditMode ? 'Modifier le rapport' : 'Nouveau rapport'}
                </h1>
                <p className="text-sm text-slate-400">
                  {(report.sections || []).length} section(s) • {(report.images || []).length} image(s)
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Boutons Undo/Redo */}
              <div className="flex items-center gap-1 border-r border-slate-700 pr-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={undo}
                  disabled={historyIndex <= 0}
                  className="text-slate-400 hover:text-white h-8 w-8 p-0"
                  title="Annuler (Ctrl+Z)"
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={redo}
                  disabled={historyIndex >= history.length - 1}
                  className="text-slate-400 hover:text-white h-8 w-8 p-0"
                  title="Rétablir (Ctrl+Y)"
                >
                  <ArrowLeft className="w-4 h-4 rotate-180" />
                </Button>
              </div>
              
              {/* Aide */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    onClick={() => setShowHelp(true)}
                    className="text-slate-400 hover:text-white"
                  >
                    <BookOpen className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="bg-slate-800 text-white border-slate-600">
                  <p>📖 Mode d'emploi</p>
                </TooltipContent>
              </Tooltip>
              
              {/* Assistant */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    onClick={() => setShowAssistant(true)}
                    className="text-amber-400 hover:text-amber-300"
                  >
                    <Mic className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="bg-slate-800 text-white border-slate-600">
                  <p>🤖 Assistant IA - Aide à la rédaction</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    onClick={() => setShowPreview(true)}
                    className="border-slate-600 text-slate-300"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Aperçu
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="bg-slate-800 text-white border-slate-600">
                  <p>👁️ Prévisualiser le rapport en temps réel</p>
                </TooltipContent>
              </Tooltip>
              
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={saveReport}
                    disabled={isSaving}
                    className="bg-cyan-600 hover:bg-cyan-500"
                  >
                    {isSaving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : saveSuccess ? (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    {isSaving ? 'Sauvegarde...' : saveSuccess ? 'Sauvegardé !' : 'Sauvegarder'}
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="bg-slate-800 text-white border-slate-600">
                  <p>💾 Sauvegarder (Ctrl+S)</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Colonne principale - Sections */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Informations générales */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-cyan-400" />
                  Informations générales
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Titre du rapport *</Label>
                    <Input
                      value={report.title}
                      onChange={(e) => setReport(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Ex: Mesures acoustiques événement..."
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Référence</Label>
                    <Input
                      value={report.reference}
                      onChange={(e) => setReport(prev => ({ ...prev, reference: e.target.value }))}
                      placeholder="Ex: RAP-2025-001"
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Champ Destinataire avec autocomplétion */}
                  <div className="relative">
                    <Label className="text-slate-300">Destinataire</Label>
                    <Input
                      value={clientSearch || report.destinataire.societe}
                      onChange={(e) => {
                        const value = e.target.value;
                        setClientSearch(value);
                        setReport(prev => ({ 
                          ...prev, 
                          destinataire: { ...prev.destinataire, societe: value }
                        }));
                        searchClients(value);
                      }}
                      onFocus={() => clientSuggestions.length > 0 && setShowClientSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowClientSuggestions(false), 200)}
                      placeholder="Tapez pour rechercher un client..."
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                    {showClientSuggestions && clientSuggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-md shadow-lg max-h-60 overflow-auto">
                        {clientSuggestions.map((client) => (
                          <div
                            key={client.client_id}
                            className="px-3 py-2 hover:bg-slate-700 cursor-pointer border-b border-slate-700 last:border-b-0"
                            onMouseDown={() => selectClient(client)}
                          >
                            <div className="text-white font-medium">{client.name}</div>
                            <div className="text-xs text-slate-400">
                              {client.city && <span>{client.city}</span>}
                              {client.email && <span> • {client.email}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Champ Site avec autocomplétion */}
                  <div className="relative">
                    <Label className="text-slate-300">Site</Label>
                    <Input
                      value={siteSearch || report.site.nom}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSiteSearch(value);
                        setReport(prev => ({ 
                          ...prev, 
                          site: { ...prev.site, nom: value }
                        }));
                        searchSites(value);
                      }}
                      onFocus={() => siteSuggestions.length > 0 && setShowSiteSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSiteSuggestions(false), 200)}
                      placeholder="Tapez pour rechercher un site..."
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                    {showSiteSuggestions && siteSuggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-md shadow-lg max-h-60 overflow-auto">
                        {siteSuggestions.map((site, idx) => (
                          <div
                            key={idx}
                            className="px-3 py-2 hover:bg-slate-700 cursor-pointer border-b border-slate-700 last:border-b-0"
                            onMouseDown={() => selectSite(site)}
                          >
                            <div className="text-white font-medium">{site.nom}</div>
                            {site.adresse && (
                              <div className="text-xs text-slate-400">{site.adresse}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Date début mesures</Label>
                    <Input
                      type="date"
                      value={report.periode.debut}
                      onChange={(e) => setReport(prev => ({ 
                        ...prev, 
                        periode: { ...prev.periode, debut: e.target.value }
                      }))}
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Date fin mesures</Label>
                    <Input
                      type="date"
                      value={report.periode.fin}
                      onChange={(e) => setReport(prev => ({ 
                        ...prev, 
                        periode: { ...prev.periode, fin: e.target.value }
                      }))}
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Barre d'outils rapide */}
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                onClick={() => addSection()}
                className="bg-cyan-600 hover:bg-cyan-500"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter section
              </Button>
              
              <Button
                variant="outline"
                onClick={() => setShowLibrary(true)}
                className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Bibliothèque
              </Button>
              
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <Image className="w-4 h-4 mr-2" />
                Importer image
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>

            {/* Liste des sections */}
            <div className="space-y-4">
              {(report.sections || []).length === 0 ? (
                <Card className="bg-slate-800/30 border-slate-700 border-dashed">
                  <CardContent className="py-12 text-center">
                    <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400 mb-4">Aucune section pour le moment</p>
                    <div className="flex items-center justify-center gap-3">
                      <Button
                        onClick={() => addSection()}
                        className="bg-cyan-600 hover:bg-cyan-500"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Créer une section
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setShowLibrary(true)}
                        className="border-cyan-500/50 text-cyan-400"
                      >
                        <BookOpen className="w-4 h-4 mr-2" />
                        Depuis la bibliothèque
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                (report.sections || []).map((section, index) => (
                  <Card key={section.id} className="bg-slate-800/50 border-slate-700">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1">
                          <div className="flex flex-col gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => moveSection(index, 'up')}
                              disabled={index === 0}
                              className="h-6 w-6 p-0 text-slate-500 hover:text-white"
                            >
                              <ChevronUp className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => moveSection(index, 'down')}
                              disabled={index === (report.sections || []).length - 1}
                              className="h-6 w-6 p-0 text-slate-500 hover:text-white"
                            >
                              <ChevronDown className="w-4 h-4" />
                            </Button>
                          </div>
                          <Input
                            value={section.title}
                            onChange={(e) => updateSection(section.id, 'title', e.target.value)}
                            className="bg-transparent border-none text-white font-semibold text-lg p-0 h-auto focus:ring-0"
                            placeholder="Titre de la section"
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => saveSectionToLibrary(section)}
                            className="text-slate-400 hover:text-green-400"
                            title="Sauvegarder dans la bibliothèque"
                          >
                            <BookOpen className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => duplicateSection(section)}
                            className="text-slate-400 hover:text-cyan-400"
                            title="Dupliquer"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteSection(section.id)}
                            className="text-slate-400 hover:text-red-400"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Textarea
                        value={section.content}
                        onChange={(e) => updateSection(section.id, 'content', e.target.value)}
                        placeholder="Contenu de la section... (Markdown supporté)"
                        className="bg-slate-900 border-slate-600 text-white min-h-[200px] font-mono text-sm"
                      />
                      
                      {/* Images de cette section */}
                      {(report.images || []).filter(img => img.sectionId === section.id).length > 0 && (
                        <div className="mt-3 border-t border-slate-700 pt-3">
                          <p className="text-xs text-slate-400 mb-2">Images de cette section :</p>
                          <div className="grid grid-cols-3 gap-2">
                            {(report.images || []).filter(img => img.sectionId === section.id).map(img => (
                              <div key={img.id} className="relative group">
                                <img
                                  src={img.data}
                                  alt={img.name}
                                  className="w-full h-20 object-cover rounded border border-slate-600"
                                />
                                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      // Détacher l'image de la section (retour au panneau global)
                                      setReport(prev => ({
                                        ...prev,
                                        images: prev.images.map(i => 
                                          i.id === img.id ? { ...i, sectionId: null } : i
                                        )
                                      }));
                                      toast.info('Image déplacée vers le panneau images');
                                    }}
                                    className="text-cyan-400 hover:text-cyan-300"
                                    title="Déplacer vers le panneau images"
                                  >
                                    <ArrowRight className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => deleteImage(img.id)}
                                    className="text-red-400 hover:text-red-300"
                                    title="Supprimer"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                                <p className="text-xs text-slate-500 mt-1 truncate">{img.name}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Barre d'outils de la section */}
                      <div className="flex items-center gap-2 mt-3 flex-wrap border-t border-slate-700 pt-3">
                        {/* Import image direct */}
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUploadForSection(e, section.id)}
                            className="hidden"
                          />
                          <span className="inline-flex items-center gap-1 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm rounded px-3 py-1.5 transition-colors">
                            <Image className="w-4 h-4" />
                            Image
                          </span>
                        </label>
                        
                        {/* Import fichier universel */}
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="*/*"
                            onChange={(e) => handleFileImportForSection(e, section.id)}
                            className="hidden"
                          />
                          <span className="inline-flex items-center gap-1 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm rounded px-3 py-1.5 transition-colors">
                            <Upload className="w-4 h-4" />
                            Fichier
                          </span>
                        </label>

                        {/* Insérer image existante */}
                        {(report.images || []).length > 0 && (
                          <select
                            onChange={(e) => {
                              if (e.target.value) {
                                insertImageInSection(section.id, e.target.value);
                                e.target.value = '';
                              }
                            }}
                            className="bg-slate-800 border border-slate-600 text-slate-300 text-sm rounded px-2 py-1.5"
                          >
                            <option value="">Insérer image existante...</option>
                            {(report.images || []).map(img => (
                              <option key={img.id} value={img.id}>{img.name}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>

          {/* Colonne latérale - Ressources et infos */}
          <div className="space-y-6">
            
            {/* Gestionnaire de Ressources */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-white flex items-center justify-between text-base">
                  <span className="flex items-center gap-2">
                    <FileImage className="w-4 h-4 text-cyan-400" />
                    Ressources
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowRessourcesManager(!showRessourcesManager)}
                    className="text-slate-400 hover:text-white h-6 px-2"
                  >
                    {showRessourcesManager ? '−' : '+'}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {/* Liste des dossiers */}
                <div className="space-y-1 mb-3">
                  {(report.ressources?.dossiers || []).map(dossier => (
                    <div
                      key={dossier.id}
                      className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                        selectedDossier === dossier.id 
                          ? 'bg-cyan-600/30 border border-cyan-500/50' 
                          : 'hover:bg-slate-700/50'
                      }`}
                      onClick={() => setSelectedDossier(dossier.id)}
                    >
                      <span className="text-sm text-slate-300 flex items-center gap-2">
                        <FileImage className="w-3 h-3" />
                        {dossier.name}
                        <span className="text-xs text-slate-500">({dossier.files.length})</span>
                      </span>
                      {!['photos', 'documents', 'certificats'].includes(dossier.id) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); deleteDossier(dossier.id); }}
                          className="h-5 w-5 p-0 text-slate-500 hover:text-red-400"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Créer un nouveau dossier */}
                {showRessourcesManager && (
                  <div className="flex gap-1 mb-3">
                    <Input
                      value={newDossierName}
                      onChange={(e) => setNewDossierName(e.target.value)}
                      placeholder="Nouveau dossier..."
                      className="h-7 text-xs bg-slate-900 border-slate-600"
                      onKeyPress={(e) => e.key === 'Enter' && createDossier(newDossierName)}
                    />
                    <Button
                      size="sm"
                      onClick={() => createDossier(newDossierName)}
                      className="h-7 px-2 bg-cyan-600"
                    >
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>
                )}
                
                {/* Contenu du dossier sélectionné */}
                {selectedDossier && (
                  <div className="border-t border-slate-700 pt-3">
                    <input
                      type="file"
                      multiple
                      onChange={(e) => handleDossierUpload(e, selectedDossier)}
                      className="hidden"
                      id={`dossier-upload-${selectedDossier}`}
                    />
                    <label
                      htmlFor={`dossier-upload-${selectedDossier}`}
                      className="flex items-center justify-center gap-2 w-full py-2 border border-dashed border-slate-600 rounded cursor-pointer hover:bg-slate-700/50 transition-colors text-slate-400 text-xs mb-2"
                    >
                      <Upload className="w-3 h-3" />
                      Ajouter des fichiers
                    </label>
                    
                    {/* Fichiers du dossier */}
                    {(() => {
                      const dossier = (report.ressources?.dossiers || []).find(d => d.id === selectedDossier);
                      if (!dossier || dossier.files.length === 0) {
                        return (
                          <p className="text-slate-500 text-xs text-center py-2">Dossier vide</p>
                        );
                      }
                      return (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {dossier.files.map(file => (
                            <div key={file.id} className="relative group">
                              {file.type.startsWith('image/') ? (
                                <img
                                  src={file.data}
                                  alt={file.name}
                                  className="w-full h-20 object-cover rounded border border-slate-600"
                                />
                              ) : (
                                <div className="w-full h-20 bg-slate-900 rounded border border-slate-600 flex items-center justify-center">
                                  <FileText className="w-8 h-8 text-slate-500" />
                                </div>
                              )}
                              <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1 rounded">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => deleteFileFromDossier(selectedDossier, file.id)}
                                  className="h-6 w-6 p-0 text-red-400"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                              <p className="text-xs text-slate-400 truncate mt-1">{file.name}</p>
                              
                              {/* Assigner à une section */}
                              {(report.sections || []).length > 0 && (
                                <select
                                  className="w-full mt-1 text-xs bg-slate-700 border border-slate-600 rounded p-1 text-slate-300"
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      insertFileInSection(e.target.value, selectedDossier, file.id);
                                    }
                                  }}
                                  defaultValue=""
                                >
                                  <option value="">→ Insérer dans section...</option>
                                  {(report.sections || []).map(s => (
                                    <option key={s.id} value={s.id}>{s.title || 'Section sans titre'}</option>
                                  ))}
                                </select>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                    
                    {/* Bouton créer annexe */}
                    {(() => {
                      const dossier = (report.ressources?.dossiers || []).find(d => d.id === selectedDossier);
                      if (dossier && dossier.files.length > 0) {
                        return (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => createAnnexeFromDossier(selectedDossier)}
                            className="w-full mt-2 text-xs border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                          >
                            <FileText className="w-3 h-3 mr-1" />
                            Créer une annexe
                          </Button>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Panneau Annexes */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2 text-base">
                  <FileText className="w-4 h-4 text-orange-400" />
                  Annexes ({(report.annexes || []).length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <input
                  type="file"
                  accept=".pdf"
                  multiple
                  onChange={handleAnnexeUpload}
                  className="hidden"
                  id="annexe-upload"
                />
                <label
                  htmlFor="annexe-upload"
                  className="flex items-center justify-center gap-2 w-full py-2 px-3 border border-dashed border-slate-600 rounded cursor-pointer hover:bg-slate-700/50 transition-colors text-slate-400 text-sm mb-3"
                >
                  <Upload className="w-4 h-4" />
                  Ajouter des annexes (PDF)
                </label>
                
                {(report.annexes || []).length > 0 && (
                  <div className="space-y-2">
                    {(report.annexes || []).map((annexe, idx) => (
                      <div key={annexe.id} className="flex items-center gap-2 p-2 bg-slate-900 rounded border border-slate-700">
                        <FileText className="w-4 h-4 text-orange-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-white truncate">{annexe.name}</p>
                          <p className="text-xs text-slate-500">{(annexe.size / 1024).toFixed(0)} Ko</p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteAnnexe(annexe.id)}
                          className="text-red-400 hover:text-red-300 p-1"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Raccourcis bibliothèque */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2 text-base">
                  <BookOpen className="w-4 h-4 text-cyan-400" />
                  Insertion rapide
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setLibraryTab('reglementation');
                    setShowLibrary(true);
                  }}
                  className="w-full justify-start border-slate-600 text-slate-300"
                >
                  <Scale className="w-4 h-4 mr-2" />
                  Réglementation
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setLibraryTab('methodologies');
                    setShowLibrary(true);
                  }}
                  className="w-full justify-start border-slate-600 text-slate-300"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Méthodologies
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => addSectionFromLibrary({}, 'glossaire')}
                  className="w-full justify-start border-slate-600 text-slate-300"
                >
                  <List className="w-4 h-4 mr-2" />
                  Glossaire complet
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setLibraryTab('sections');
                    setShowLibrary(true);
                  }}
                  className="w-full justify-start border-slate-600 text-slate-300"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Sections types
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Modal Bibliothèque */}
      <Dialog open={showLibrary} onOpenChange={setShowLibrary}>
        <DialogContent className="max-w-3xl max-h-[80vh] bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-cyan-400" />
              Bibliothèque de contenus
            </DialogTitle>
          </DialogHeader>
          
          <Tabs value={libraryTab} onValueChange={setLibraryTab}>
            <TabsList className="bg-slate-800">
              <TabsTrigger value="mes-contenus">⭐ Mes contenus</TabsTrigger>
              <TabsTrigger value="sections">Sections</TabsTrigger>
              <TabsTrigger value="reglementation">Réglementation</TabsTrigger>
              <TabsTrigger value="methodologies">Méthodologies</TabsTrigger>
              <TabsTrigger value="glossaire">Glossaire</TabsTrigger>
            </TabsList>
            
            <ScrollArea className="h-[50vh] mt-4">
              {/* MES CONTENUS PERSONNALISÉS */}
              <TabsContent value="mes-contenus" className="space-y-2">
                <Card
                  className="bg-green-900/30 border-green-500/50 cursor-pointer hover:bg-green-900/50 transition-colors mb-4"
                  onClick={() => setShowAddToLibrary(true)}
                >
                  <CardContent className="py-3 flex items-center justify-between">
                    <span className="text-green-300 font-medium">➕ Créer un nouveau contenu</span>
                    <Plus className="w-4 h-4 text-green-400" />
                  </CardContent>
                </Card>
                
                {customLibrary.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Aucun contenu personnalisé</p>
                    <p className="text-sm mt-1">Créez ou sauvegardez des sections pour les réutiliser</p>
                  </div>
                ) : (
                  customLibrary.map(item => (
                    <Card
                      key={item.item_id}
                      className="bg-slate-800 border-slate-700 hover:border-cyan-500/50 transition-colors"
                    >
                      <CardContent className="py-3">
                        <div className="flex items-center justify-between mb-2">
                          <span 
                            className="text-white font-medium cursor-pointer hover:text-cyan-400"
                            onClick={() => addSectionFromLibrary(item, 'custom')}
                          >
                            {item.title}
                          </span>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => addSectionFromLibrary(item, 'custom')}
                              className="text-cyan-400 hover:text-cyan-300 h-7 px-2"
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteFromLibrary(item.item_id)}
                              className="text-red-400 hover:text-red-300 h-7 px-2"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-slate-400 text-sm line-clamp-2">{(item.content || '').substring(0, 150)}...</p>
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>

              <TabsContent value="sections" className="space-y-2">
                {contentLibrary.sections.map(item => (
                  <Card
                    key={item.id}
                    className="bg-slate-800 border-slate-700 cursor-pointer hover:border-cyan-500/50 transition-colors"
                    onClick={() => addSectionFromLibrary(item, 'sections')}
                  >
                    <CardContent className="py-3 flex items-center justify-between">
                      <span className="text-white">{item.title}</span>
                      <Plus className="w-4 h-4 text-cyan-400" />
                    </CardContent>
                  </Card>
                ))}
              </TabsContent>
              
              <TabsContent value="reglementation" className="space-y-2">
                {contentLibrary.reglementation.map(item => (
                  <Card
                    key={item.id}
                    className="bg-slate-800 border-slate-700 cursor-pointer hover:border-cyan-500/50 transition-colors"
                    onClick={() => addSectionFromLibrary(item, 'reglementation')}
                  >
                    <CardContent className="py-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-medium">{item.title}</span>
                        <Plus className="w-4 h-4 text-cyan-400" />
                      </div>
                      <p className="text-slate-400 text-sm line-clamp-2">{item.content.substring(0, 150)}...</p>
                    </CardContent>
                  </Card>
                ))}
              </TabsContent>
              
              <TabsContent value="methodologies" className="space-y-2">
                {contentLibrary.methodologies.map(item => (
                  <Card
                    key={item.id}
                    className="bg-slate-800 border-slate-700 cursor-pointer hover:border-cyan-500/50 transition-colors"
                    onClick={() => addSectionFromLibrary(item, 'methodologies')}
                  >
                    <CardContent className="py-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-medium">{item.title}</span>
                        <Plus className="w-4 h-4 text-cyan-400" />
                      </div>
                      <p className="text-slate-400 text-sm line-clamp-2">{item.content.substring(0, 150)}...</p>
                    </CardContent>
                  </Card>
                ))}
              </TabsContent>
              
              <TabsContent value="glossaire" className="space-y-2">
                <Card
                  className="bg-cyan-900/30 border-cyan-500/50 cursor-pointer hover:bg-cyan-900/50 transition-colors mb-4"
                  onClick={() => addSectionFromLibrary({}, 'glossaire')}
                >
                  <CardContent className="py-3 flex items-center justify-between">
                    <span className="text-cyan-300 font-medium">➕ Insérer tout le glossaire</span>
                    <Plus className="w-4 h-4 text-cyan-400" />
                  </CardContent>
                </Card>
                
                {contentLibrary.glossaire.map((item, idx) => (
                  <Card key={idx} className="bg-slate-800 border-slate-700">
                    <CardContent className="py-2">
                      <span className="text-cyan-400 font-medium">{item.terme}</span>
                      <span className="text-slate-400 text-sm ml-2">: {item.definition}</span>
                    </CardContent>
                  </Card>
                ))}
              </TabsContent>
            </ScrollArea>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Modal Ajouter à la bibliothèque */}
      <Dialog open={showAddToLibrary} onOpenChange={setShowAddToLibrary}>
        <DialogContent className="max-w-xl bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-400" />
              Ajouter à ma bibliothèque
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300">Titre *</Label>
              <Input
                value={newLibraryItem.title}
                onChange={(e) => setNewLibraryItem(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Ex: Introduction type rapport événementiel"
                className="bg-slate-800 border-slate-600 text-white"
              />
            </div>
            
            <div>
              <Label className="text-slate-300">Catégorie</Label>
              <select
                value={newLibraryItem.category}
                onChange={(e) => setNewLibraryItem(prev => ({ ...prev, category: e.target.value }))}
                className="w-full bg-slate-800 border border-slate-600 text-white rounded-md px-3 py-2"
              >
                <option value="custom">Personnalisé</option>
                <option value="sections">Section type</option>
                <option value="reglementation">Réglementation</option>
                <option value="methodologies">Méthodologie</option>
              </select>
            </div>
            
            <div>
              <Label className="text-slate-300">Contenu *</Label>
              <Textarea
                value={newLibraryItem.content}
                onChange={(e) => setNewLibraryItem(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Entrez le contenu réutilisable..."
                className="bg-slate-800 border-slate-600 text-white min-h-[200px]"
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowAddToLibrary(false)}
                className="border-slate-600 text-slate-300"
              >
                Annuler
              </Button>
              <Button
                onClick={addToLibrary}
                className="bg-green-600 hover:bg-green-500"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter à la bibliothèque
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Mode d'emploi */}
      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent className="max-w-2xl max-h-[80vh] bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-cyan-400" />
              Mode d'emploi - Éditeur de rapports
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="h-[60vh] pr-4">
            <div className="space-y-6 text-slate-300">
              <section>
                <h3 className="text-lg font-semibold text-white mb-2">🚀 Démarrage rapide</h3>
                <ol className="list-decimal list-inside space-y-1 text-sm">
                  <li>Remplissez les informations générales (titre, client, site)</li>
                  <li>Ajoutez des sections via le bouton "Ajouter section" ou la bibliothèque</li>
                  <li>Personnalisez le contenu de chaque section</li>
                  <li>Sauvegardez régulièrement (Ctrl+S)</li>
                </ol>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-white mb-2">📚 Bibliothèque</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li><strong>Sections types</strong> : Structure classique d'un rapport</li>
                  <li><strong>Réglementation</strong> : Textes réglementaires pré-rédigés</li>
                  <li><strong>Méthodologies</strong> : Protocoles de mesure standards</li>
                  <li><strong>Glossaire</strong> : Définitions techniques</li>
                  <li><strong>Mes contenus</strong> : Vos contenus personnalisés sauvegardés</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-white mb-2">🔧 Actions sur les sections</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li><strong>↑↓ Flèches</strong> : Réorganiser l'ordre des sections</li>
                  <li><strong>📖 Livre</strong> : Sauvegarder dans votre bibliothèque</li>
                  <li><strong>📋 Copier</strong> : Dupliquer la section</li>
                  <li><strong>🗑️ Corbeille</strong> : Supprimer la section</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-white mb-2">📷 Import de fichiers</h3>
                <p className="text-sm mb-2">Dans chaque section, vous pouvez importer :</p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li><strong>Images</strong> : JPG, PNG, GIF...</li>
                  <li><strong>Fichiers texte</strong> : TXT, MD, CSV (contenu inséré)</li>
                  <li><strong>Autres fichiers</strong> : PDF, Word (joints en annexe)</li>
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-white mb-2">⌨️ Raccourcis clavier</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><kbd className="bg-slate-700 px-2 py-1 rounded">Ctrl+S</kbd> Sauvegarder</div>
                  <div><kbd className="bg-slate-700 px-2 py-1 rounded">Ctrl+Z</kbd> Annuler</div>
                  <div><kbd className="bg-slate-700 px-2 py-1 rounded">Ctrl+Y</kbd> Rétablir</div>
                  <div><kbd className="bg-slate-700 px-2 py-1 rounded">Ctrl+Shift+Z</kbd> Rétablir</div>
                </div>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-white mb-2">💡 Conseils</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Utilisez la bibliothèque pour gagner du temps</li>
                  <li>Sauvegardez vos sections récurrentes dans "Mes contenus"</li>
                  <li>Le contenu supporte le format Markdown</li>
                  <li>Prévisualisez votre rapport avant export PDF</li>
                </ul>
              </section>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Modal Assistant */}
      <Dialog open={showAssistant} onOpenChange={setShowAssistant}>
        <DialogContent className="max-w-xl bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Mic className="w-5 h-5 text-amber-400" />
              Assistant - Aide à la rédaction
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-slate-400 text-sm">
              Posez votre question sur la rédaction de rapports acoustiques, la réglementation, ou l'utilisation de l'éditeur.
            </p>
            
            <Textarea
              value={assistantMessage}
              onChange={(e) => setAssistantMessage(e.target.value)}
              placeholder="Ex: Comment calculer l'émergence ? Quelle réglementation pour un bar ? Comment structurer mon rapport ?"
              className="bg-slate-800 border-slate-600 text-white min-h-[100px]"
            />
            
            <div className="flex justify-between items-center">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAssistantMessage("Quelle réglementation pour un événement en plein air ?")}
                  className="border-slate-600 text-slate-400 text-xs"
                >
                  Événement plein air
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAssistantMessage("Comment structurer un rapport de mesures d'émergence ?")}
                  className="border-slate-600 text-slate-400 text-xs"
                >
                  Structure rapport
                </Button>
              </div>
              
              <Button
                onClick={() => {
                  toast.info("L'assistant IA sera bientôt disponible !");
                  // TODO: Intégrer avec le chatbot IA
                }}
                className="bg-amber-600 hover:bg-amber-500"
              >
                Demander
              </Button>
            </div>

            <div className="border-t border-slate-700 pt-4">
              <p className="text-slate-500 text-xs">
                💡 Pour une aide plus complète, utilisez le chatbot accessible depuis le bouton en bas à droite de l'écran.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Aperçu temps réel */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden bg-white border-slate-300 p-0">
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white p-4 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">{report.title || 'Rapport sans titre'}</h2>
              <p className="text-slate-300 text-sm">Aperçu en temps réel • {(report.sections || []).length} section(s)</p>
            </div>
            <div className="flex gap-2">
              {/* Bouton Télécharger PDF */}
              {reportId ? (
                <Button
                  size="sm"
                  onClick={downloadPdfViaPost}
                  className="bg-green-600 hover:bg-green-500"
                >
                  <Save className="w-4 h-4 mr-1" />
                  Télécharger PDF
                </Button>
              ) : (
                <Button
                  size="sm"
                  disabled
                  className="bg-slate-600 cursor-not-allowed"
                  title="Sauvegardez d'abord le rapport"
                >
                  <Save className="w-4 h-4 mr-1" />
                  PDF (sauvegarder d'abord)
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrintPreview}
                className="border-white/30 text-white hover:bg-white/10"
              >
                <Printer className="w-4 h-4 mr-1" />
                Imprimer
              </Button>
              <Button
                size="sm"
                onClick={() => setShowPreview(false)}
                className="bg-cyan-600 hover:bg-cyan-500"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          <ScrollArea className="h-[calc(90vh-80px)]">
            <div className="p-8 bg-white print:p-0" id="preview-content">
              {/* En-tête du rapport */}
              <div className="border-b-2 border-cyan-600 pb-6 mb-8">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-slate-500 text-sm uppercase tracking-wider">Rapport d'expertise acoustique</p>
                    <h1 className="text-3xl font-bold text-slate-800 mt-1">{report.title || 'Titre du rapport'}</h1>
                  </div>
                  <div className="text-right">
                    <p className="text-cyan-600 font-semibold">{report.reference || 'Réf: -'}</p>
                    <p className="text-slate-500 text-sm">
                      {report.periode?.debut ? new Date(report.periode.debut).toLocaleDateString('fr-FR') : 'Date: -'}
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-6 mt-6">
                  <div className="bg-slate-50 p-4 rounded">
                    <p className="text-slate-500 text-xs uppercase mb-1">Destinataire</p>
                    <p className="font-semibold text-slate-700">{report.destinataire?.societe || '-'}</p>
                    {report.destinataire?.adresse && (
                      <p className="text-slate-600 text-sm">{report.destinataire.adresse}</p>
                    )}
                  </div>
                  <div className="bg-slate-50 p-4 rounded">
                    <p className="text-slate-500 text-xs uppercase mb-1">Site</p>
                    <p className="font-semibold text-slate-700">{report.site?.nom || '-'}</p>
                    {report.site?.adresse && (
                      <p className="text-slate-600 text-sm">{report.site.adresse}</p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Sections du rapport */}
              {(report.sections || []).length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune section ajoutée</p>
                  <p className="text-sm">Ajoutez des sections pour voir l'aperçu</p>
                </div>
              ) : (
                <div className="space-y-8">
                  {(report.sections || []).map((section, index) => {
                    // Remplacer les placeholders [IMAGE: nom] par les vraies images
                    const sectionImages = (report.images || []).filter(img => img.sectionId === section.id);
                    let contentParts = [];
                    let content = section.content || '';
                    
                    // Parser le contenu pour trouver les placeholders d'images
                    const imageRegex = /\[IMAGE:\s*([^\]]+)\]/g;
                    let lastIndex = 0;
                    let match;
                    
                    while ((match = imageRegex.exec(content)) !== null) {
                      // Ajouter le texte avant le placeholder
                      if (match.index > lastIndex) {
                        contentParts.push({
                          type: 'text',
                          content: content.substring(lastIndex, match.index)
                        });
                      }
                      
                      // Trouver l'image correspondante
                      const imageName = match[1].trim();
                      const image = sectionImages.find(img => img.name === imageName);
                      if (image) {
                        contentParts.push({
                          type: 'image',
                          image: image
                        });
                      } else {
                        contentParts.push({
                          type: 'text',
                          content: match[0]
                        });
                      }
                      
                      lastIndex = match.index + match[0].length;
                    }
                    
                    // Ajouter le reste du texte
                    if (lastIndex < content.length) {
                      contentParts.push({
                        type: 'text',
                        content: content.substring(lastIndex)
                      });
                    }
                    
                    // Si pas de placeholders trouvés, utiliser le contenu brut
                    if (contentParts.length === 0) {
                      contentParts.push({ type: 'text', content: content });
                    }
                    
                    return (
                      <div key={section.id} className="page-break-inside-avoid">
                        <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                          <span className="bg-cyan-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">
                            {index + 1}
                          </span>
                          {section.title || 'Section sans titre'}
                        </h2>
                        
                        <div className="text-slate-700 leading-relaxed pl-10">
                          {contentParts.map((part, idx) => (
                            part.type === 'text' ? (
                              <span key={idx} className="whitespace-pre-wrap">{part.content}</span>
                            ) : (
                              <div key={idx} className="my-4">
                                <img
                                  src={part.image.data}
                                  alt={part.image.name}
                                  className="max-w-full h-auto rounded border border-slate-200"
                                />
                                <p className="text-xs text-slate-500 mt-1">{part.image.name}</p>
                              </div>
                            )
                          ))}
                        </div>
                        
                        {/* Images de la section non placées dans le texte */}
                        {sectionImages.filter(img => !content.includes(`[IMAGE: ${img.name}]`)).length > 0 && (
                          <div className="mt-4 pl-10">
                            <p className="text-xs text-slate-500 mb-2">Images de la section :</p>
                            <div className="grid grid-cols-2 gap-4">
                              {sectionImages.filter(img => !content.includes(`[IMAGE: ${img.name}]`)).map(img => (
                                <div key={img.id} className="border rounded overflow-hidden">
                                  <img
                                    src={img.data}
                                    alt={img.name}
                                    className="w-full h-48 object-cover"
                                  />
                                  <p className="text-xs text-slate-500 p-2 bg-slate-50">{img.name}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Images globales (non assignées) */}
              {(report.images || []).filter(img => !img.sectionId).length > 0 && (
                <div className="mt-12 pt-8 border-t-2 border-slate-200">
                  <h2 className="text-xl font-bold text-slate-800 mb-4">Annexes - Images</h2>
                  <div className="grid grid-cols-3 gap-4">
                    {(report.images || []).filter(img => !img.sectionId).map(img => (
                      <div key={img.id} className="border rounded overflow-hidden">
                        <img
                          src={img.data}
                          alt={img.name}
                          className="w-full h-32 object-cover"
                        />
                        <p className="text-xs text-slate-500 p-2 bg-slate-50">{img.name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Pied de page */}
              <div className="mt-12 pt-6 border-t border-slate-200 text-center text-slate-400 text-sm">
                <p>Rapport généré le {new Date().toLocaleDateString('fr-FR')} • CF AUDIO</p>
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
};

export default ReportEditorV2;
